import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  BankOutlined,
  WalletOutlined
} from '@ant-design/icons';
import axios from 'axios';

// Instance ber-token. Endpoint yang dipakai halaman ini SEBELUMNYA terbuka tanpa
// autentikasi, jadi panggilannya memakai axios polos. Setelah router dikunci
// (24 Okt 2026), tanpa header Authorization semuanya dijawab 401.
const adminApi = axios.create();
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});


const { Option } = Select;

const PaymentMethods = () => {
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const response = await adminApi.get('/api/payment-methods');
      setPaymentMethods(response.data);
    } catch (error) {
      message.error('Gagal memuat metode pembayaran');
      console.error('Fetch payment methods error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingMethod(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingMethod(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.delete(`/api/payment-methods/${id}`);
      message.success('Metode pembayaran berhasil dihapus');
      fetchPaymentMethods();
    } catch (error) {
      message.error('Gagal menghapus metode pembayaran');
      console.error('Delete payment method error:', error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingMethod) {
        await adminApi.put(`/api/payment-methods/${editingMethod.id}`, values);
        message.success('Metode pembayaran berhasil diperbarui');
      } else {
        await adminApi.post('/api/payment-methods', values);
        message.success('Metode pembayaran berhasil ditambahkan');
      }
      setModalVisible(false);
      fetchPaymentMethods();
    } catch (error) {
      message.error(editingMethod ? 'Gagal memperbarui metode pembayaran' : 'Gagal menambahkan metode pembayaran');
      console.error('Submit payment method error:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'credit_card':
      case 'debit_card':
        return <CreditCardOutlined />;
      case 'bank_transfer':
      case 'virtual_account':
        return <BankOutlined />;
      case 'ewallet':
        return <WalletOutlined />;
      default:
        return <CreditCardOutlined />;
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'credit_card':
        return 'Kartu Kredit';
      case 'debit_card':
        return 'Kartu Debit';
      case 'bank_transfer':
        return 'Transfer Bank';
      case 'virtual_account':
        return 'Virtual Account';
      case 'ewallet':
        return 'E-Wallet';
      default:
        return type;
    }
  };

  const columns = [
    {
      title: 'Icon',
      dataIndex: 'type',
      key: 'type',
      width: 60,
      render: (type) => (
        <div style={{ fontSize: '18px', textAlign: 'center' }}>
          {getTypeIcon(type)}
        </div>
      ),
    },
    {
      title: 'Nama',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Tipe',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => (
        <Tag icon={getTypeIcon(type)} color="blue">
          {getTypeText(type)}
        </Tag>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 150,
    },
    {
      title: 'Fee Fixed',
      dataIndex: 'fee_fixed',
      key: 'fee_fixed',
      width: 120,
      render: (fee) => formatCurrency(fee),
    },
    {
      title: 'Fee %',
      dataIndex: 'fee_percentage',
      key: 'fee_percentage',
      width: 100,
      render: (fee) => `${fee}%`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? 'Aktif' : 'Tidak Aktif'}
        </Tag>
      ),
    },
    {
      title: 'Tanggal Dibuat',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => new Date(date).toLocaleDateString('id-ID'),
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Apakah Anda yakin ingin menghapus metode pembayaran ini?"
            onConfirm={() => handleDelete(record.id)}
            okText="Ya"
            cancelText="Tidak"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Hapus
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>Metode Pembayaran</h1>
        <p style={{ color: '#666' }}>Kelola metode pembayaran yang tersedia</p>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Total Metode Pembayaran"
              value={paymentMethods.length}
              prefix={<CreditCardOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Metode Aktif"
              value={paymentMethods.filter(pm => pm.status === 'active').length}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Metode Tidak Aktif"
              value={paymentMethods.filter(pm => pm.status === 'inactive').length}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <h3>Daftar Metode Pembayaran</h3>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Tambah Metode
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchPaymentMethods}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={paymentMethods}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingMethod ? 'Edit Metode Pembayaran' : 'Tambah Metode Pembayaran'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Nama Metode"
                name="name"
                rules={[{ required: true, message: 'Nama metode wajib diisi' }]}
              >
                <Input placeholder="Contoh: Virtual Account BCA" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Tipe"
                name="type"
                rules={[{ required: true, message: 'Tipe metode wajib dipilih' }]}
              >
                <Select placeholder="Pilih tipe metode">
                  <Option value="credit_card">Kartu Kredit</Option>
                  <Option value="debit_card">Kartu Debit</Option>
                  <Option value="bank_transfer">Transfer Bank</Option>
                  <Option value="virtual_account">Virtual Account</Option>
                  <Option value="ewallet">E-Wallet</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Provider"
                name="provider"
                rules={[{ required: true, message: 'Provider wajib diisi' }]}
              >
                <Input placeholder="Contoh: BCA, Mandiri, OVO" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Icon URL"
                name="icon_url"
              >
                <Input placeholder="https://example.com/icon.png" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Fee Fixed"
                name="fee_fixed"
                initialValue={0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/Rp\s?|(,*)/g, '')}
                  placeholder="0"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Fee Persentase (%)"
                name="fee_percentage"
                initialValue={0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `${value}%`}
                  parser={value => value.replace('%', '')}
                  placeholder="0"
                  min={0}
                  max={100}
                />
              </Form.Item>
            </Col>
          </Row>

          {editingMethod && (
            <Form.Item
              label="Status"
              name="status"
              valuePropName="checked"
              getValueFromEvent={(checked) => checked ? 'active' : 'inactive'}
            >
              <Switch checkedChildren="Aktif" unCheckedChildren="Tidak Aktif" />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit">
                {editingMethod ? 'Update' : 'Simpan'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentMethods;